import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { FileInfo, MenuOptionValue, Paginated, PaginationQuery } from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { DkvCostImport } from "./types";

export const useDkvCostImportsApi = () => {
  const api = useApi();

  const dkvUploadCsv = useCallback(
    async (data: FormData) => {
      const url = "/admin/costs_imports/dkv";
      const response = await api.postNoResult<FormData>(url, data);
      api.mutateCache([url, `/admin/costs_imports`]);
      return response;
    },
    [api],
  );

  const useDkvCostImports = useCallback(
    (
      query: PaginationQuery,
      signal: AbortSignal,
      importId: string,
      withErrors?: boolean,
      notExported?: boolean,
      forced?: boolean,
    ) => {
      let url =
        withErrors === undefined
          ? buildUrl(`/admin/costs_imports/dkv/${importId}`, query)
          : buildUrl(`/admin/costs_imports/dkv/${importId}/${withErrors}`, query);

      const extraParams = new URLSearchParams();
      notExported !== undefined && extraParams.set("notExported", `${notExported}`);
      forced !== undefined && extraParams.set("forced", `${forced}`);
      if (extraParams.size > 0) {
        url += (url.includes("?") ? "&" : "?") + extraParams.toString();
      }

      return useSWR<Paginated<DkvCostImport>, CustomError, string>(url, (url) =>
        api.get<Paginated<DkvCostImport>>({ url, signal }),
      );
    },
    [api],
  );

  const getDkvFieldFilters = useCallback(
    (fieldName: string, signal: AbortSignal) => {
      const url = `/admin/costs_imports/dkv/filters?field=${fieldName}`;
      return useSWRMutation<MenuOptionValue[] | null, CustomError, string>(url, (url) =>
        api.get<MenuOptionValue[]>({ url, signal }),
      );
    },
    [api],
  );

  const reloadDkvLines = useCallback(
    async (id: string) => {
      const url = `/admin/costs_imports/dkv/${id}/reload`;
      await api.post(url);
      api.mutateCache([
        url,
        `/admin/costs_imports/dkv/${id}`,
        `/admin/costs_imports/dkv/${id}/true`,
        `/admin/costs_imports/dkv/${id}/false`,
      ]);
    },
    [api],
  );

  const exportExcel = useCallback(
    (id: string, signal: AbortSignal) => {
      const url = `/admin/costs_imports/dkv/${id}/export`;
      const h = new Headers();
      h.append("Accept", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
      const response = api.get<FileInfo>({ url, signal, headers: h });
      return response;
    },
    [api],
  );

  const forceCar = useCallback(
    async (id: string, carId: string, importId: string) => {
      const url = `/admin/costs_imports/dkv/${id}/row/car`;
      await api.put<{ carId: string }>(url, { carId });
      api.mutateCache([
        `/admin/costs_imports/dkv/${importId}`,
        `/admin/costs_imports/dkv/${importId}/true`,
        `/admin/costs_imports/dkv/${importId}/false`,
      ]);
    },
    [api],
  );

  const removeForcedCar = useCallback(
    async (id: string, importId: string) => {
      const url = `/admin/costs_imports/dkv/${id}/row/car/force`;
      await api.del(url);
      api.mutateCache([
        `/admin/costs_imports/dkv/${importId}`,
        `/admin/costs_imports/dkv/${importId}/true`,
        `/admin/costs_imports/dkv/${importId}/false`,
      ]);
    },
    [api],
  );

  const forceAssignment = useCallback(
    async (
      id: string,
      carId: string,
      assignmentId: string,
      assignmentType: "carDriver" | "other",
      importId: string,
    ) => {
      const url = `/admin/costs_imports/dkv/${id}/row/assignment`;
      await api.put<{ carId: string; assignmentId: string; assignmentType: string }>(url, {
        carId,
        assignmentId,
        assignmentType,
      });
      api.mutateCache([
        `/admin/costs_imports/dkv/${importId}`,
        `/admin/costs_imports/dkv/${importId}/true`,
        `/admin/costs_imports/dkv/${importId}/false`,
      ]);
    },
    [api],
  );

  const removeForcedAssignment = useCallback(
    async (id: string, carId: string, importId: string) => {
      const url = `/admin/costs_imports/dkv/${id}/row/assignment/force?carId=${carId}`;
      await api.del(url);
      api.mutateCache([
        `/admin/costs_imports/dkv/${importId}`,
        `/admin/costs_imports/dkv/${importId}/true`,
        `/admin/costs_imports/dkv/${importId}/false`,
      ]);
    },
    [api],
  );

  return {
    dkvUploadCsv,
    useDkvCostImports,
    getDkvFieldFilters,
    reloadDkvLines,
    exportExcel,
    forceCar,
    removeForcedCar,
    forceAssignment,
    removeForcedAssignment,
  };
};
